import { ChangeDetectionStrategy, Component, EventEmitter, forwardRef, Injector, Input, Output, OnChanges, ViewChild, ElementRef } from '@angular/core';
import { ValueAccessor } from '../../value-accessor/value-accessor';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { TrafficLightColor } from '../../../analysis/models/criterions/TrafficLightColor';

@Component({
    selector: 'density-input-number',
    templateUrl: './density-input-number.component.html',
    styleUrl: './density-input-number.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DensityInputNumberComponent),
            multi: true
        }
    ],
    standalone: false
})
export class DensityInputNumberComponent extends ValueAccessor<number> implements OnChanges {
    private static nextId = 0;

    @ViewChild('inputElement') inputElement!: ElementRef;

    @Input()
    inputId: string = `density-input-number-${DensityInputNumberComponent.nextId++}`;

    @Input()
    inputNumberLabelClass: string = '';

    @Input()
    isDisabled: boolean = false;

    @Input()
    isReadOnly: boolean = false;

    @Input()
    formControlName: string = '';

    @Input()
    hintText: string = '';

    @Input()
    label: string = '';

    @Input()
    translateLabel: boolean = true;

    @Input()
    placeholder: string = '0';

    @Input()
    decimals: string = '1.0-1';

    @Input()
    inputStyleClass: string = '';

    @Input()
    color: TrafficLightColor | null = null;

    @Input()
    textAlign: 'left' | 'top' = 'top';

    colorClass: string = 'sera-input-number--gray';

    @Input()
    min: number | null = 0;

    @Input()
    max: number | null = null;

    @Output()
    valueChanged = new EventEmitter<number>();

    @Output()
    inputFocus = new EventEmitter<void>();

    @Output()
    inputBlur = new EventEmitter<number | null>();

    constructor(injector: Injector) {
        super(injector);
    }

    get tooltipText(): string {
        return this.value !== null && this.value !== undefined ? String(this.value) : '';
    }

    onFocus(): void {
        this.inputFocus.emit();
    }

    onBlur(event: any): void {
        const value = event.target.value;
        this.inputBlur.emit(value !== '' ? Number(value) : null);
    }

    ngOnChanges(): void {
        this.setColorClass();
    }

    onValueChange(value: number | null): void {
        let newValue = value;

        if (newValue === null || newValue === undefined) {
            this.inputElement.nativeElement.value = '';
            this.value = null as any;
            this.valueChanged.emit(this.value);
            return;
        }

        newValue = this.max !== null ? Math.min(newValue, this.max) : newValue;
        newValue = this.min !== null ? Math.max(newValue, this.min) : newValue;

        this.inputElement.nativeElement.value = newValue;
        this.value = newValue;
        this.valueChanged.emit(this.value);
    }

    private setColorClass(): void {
        const baseClass = 'sera-input-number';
        let colorClassSuffix;

        switch (this.color) {
            case TrafficLightColor.GREEN:
                colorClassSuffix = '--green';
                break;
            case TrafficLightColor.YELLOW:
                colorClassSuffix = '--yellow';
                break;
            case TrafficLightColor.RED:
                colorClassSuffix = '--red';
                break;
            case null:
            default:
                colorClassSuffix = '--gray';
        }
        let isReadOnly = this.isReadOnly ? '--read-only' : '';
        this.colorClass = this.isDisabled ? 'sera-input-number--gray' : `${baseClass}${colorClassSuffix}${isReadOnly}`;
    }
}
